import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Contact } from '../types/Contact';
import { supabase } from '../lib/supabase';
import { listCollectionsCore, listMembershipsForCollectionsCore, bulkAddMembersCore } from '@gv/core';
import { toggleArchiveContactCore, updateContactCore, deleteContactCore } from '../lib/contactsCore';
import { Search, Plus, Star, Users, Check, FolderPlus, UserPlus, X, Trash2, Archive, Filter, FileDown } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import CollectionSelectorModal from './CollectionSelectorModal';
import { useNotifications } from '../hooks/useNotifications';
import NotificationToast from './NotificationToast';
import { exportContactToPDF, exportContactToHTML } from '../utils/contactExportUtils';

interface ContactListProps {
  contacts: Contact[];
  onContactSelect: (contact: Contact) => void;
  onAddContact: () => void;
  shouldFocusSearch?: boolean;
  onSearchFocused?: () => void;
  initialSearchTerm?: string;
  onLoadMore?: () => void;
  onLoadAll?: () => void;
  onResetToBasicView?: () => void;
  onContactUpdate?: (contact?: Contact | null) => void;
  hasMoreContacts?: boolean;
  contactsLoading?: boolean;
  initialTierFilter?: string;
  user?: any;
  collectionSelectionMode?: {
    enabled: boolean;
    collectionId: string;
    collectionName: string;
    existingMemberIds: string[];
  };
  onNavigateBack?: () => void;
  // New: server-side search provider, returns a page of contacts
  onServerSearch?: (query: string, page: number) => Promise<Contact[]>;
  // When true, hide the built-in search and filter header (we'll render an external search UI)
  hideSearchHeader?: boolean;
}

export default function ContactList({
  contacts,
  onContactSelect,
  onAddContact,
  shouldFocusSearch,
  onSearchFocused,
  initialSearchTerm,
  onLoadMore,
  onLoadAll,
  onResetToBasicView,
  onContactUpdate,
  hasMoreContacts = false,
  contactsLoading = false,
  initialTierFilter = 'all',
  user,
  collectionSelectionMode,
  onNavigateBack,
  // Ensure server-side search handler is available within component scope
  onServerSearch,
  hideSearchHeader
}: ContactListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [isAddingToCollection, setIsAddingToCollection] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('name');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [allContactsLoaded, setAllContactsLoaded] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [isCollectionSelectorOpen, setIsCollectionSelectorOpen] = useState(false);
  const [collections, setCollections] = useState<{ id: string; name: string; color: string }[]>([]);
  const [contactCollectionMap, setContactCollectionMap] = useState<Map<string, string[]>>(new Map());

  type FieldFilter = 'name' | 'company' | 'location' | 'collection' | 'status' | 'relationship' | 'updated';
  const allFieldFilters: FieldFilter[] = ['name', 'company', 'location', 'collection', 'status', 'relationship', 'updated'];
  const [selectedFieldFilters, setSelectedFieldFilters] = useState<FieldFilter[]>(allFieldFilters);
  const [showFieldDropdown, setShowFieldDropdown] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement | null>(null);
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { notification, showNotification, hideNotification } = useNotifications();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; contact: Contact } | null>(null);
  const [hiddenContactIds, setHiddenContactIds] = useState<Set<string>>(new Set());
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  // Search mode state
  const [isSearching, setIsSearching] = useState(false);
  const [searchPage, setSearchPage] = useState(0);
  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const [livePreview, setLivePreview] = useState<{ id: string; name: string; company?: string }[]>([]);

  useEffect(() => {
    const key = 'gv:removedContactIds';
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const arr: string[] = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length) {
          setHiddenContactIds(prev => {
            const next = new Set(prev);
            arr.forEach(id => next.add(id));
            return next;
          });
        }
      }
    } catch { }

    const onRemoved = (e: any) => {
      const id = e?.detail?.id as string | undefined;
      if (!id) return;
      setHiddenContactIds(prev => new Set(prev).add(id));
      try {
        const raw = sessionStorage.getItem(key);
        const arr = raw ? (JSON.parse(raw) as string[]) : [];
        if (!arr.includes(id)) {
          arr.push(id);
          sessionStorage.setItem(key, JSON.stringify(arr));
        }
      } catch { }
    };
    window.addEventListener('gv:contact-removed' as any, onRemoved as any);
    return () => window.removeEventListener('gv:contact-removed' as any, onRemoved as any);
  }, []);

  useEffect(() => {
    const q = (initialSearchTerm || '').trim();
    if (q && q !== searchTerm) setSearchTerm(q);
  }, [initialSearchTerm]);

  useEffect(() => {
    if (shouldFocusSearch && searchInputRef.current) {
      searchInputRef.current.focus();
      onSearchFocused?.();
    }
  }, [shouldFocusSearch, onSearchFocused]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const collectionsData = await listCollectionsCore(supabase as any, user.id);
        setCollections((collectionsData as any) || []);

        const membersRows = await listMembershipsForCollectionsCore(
          supabase as any,
          ((collectionsData as any) || []).map((c: any) => c.id)
        );
        const mapping = new Map<string, string[]>();
        (membersRows as any[] || []).forEach((member: any) => {
          const existing = mapping.get(member.contact_id) || [];
          mapping.set(member.contact_id, [...existing, member.collection_id]);
        });
        setContactCollectionMap(mapping);
      } catch (e) {
        console.error('Error fetching collections:', e);
      }
    })();
  }, [user]);

  // Leave the filter dropdown open until the user toggles the filter button again

  // Disable load-all on first search; switch to server-side search when available
  useEffect(() => {
    const q = (searchTerm || '').trim();
    if (!q) {
      setIsSearching(false);
      setSearchResults([]);
      setSearchPage(0);
      setSearchHasMore(false);
      setSearchLoading(false);
      setLivePreview([]);
      return;
    }

    if (!onServerSearch) {
      // Fallback to previous behavior if no server search is provided
      if (!allContactsLoaded && onLoadAll) {
        onLoadAll();
        setAllContactsLoaded(true);
      }
      return;
    }

    // Server-side search path with debounce and cancellation
    setIsSearching(true);
    setSearchLoading(true);
    setSearchPage(0);
    const controller = new AbortController();
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
    }
    abortRef.current = controller;

    const id = setTimeout(async () => {
      if (controller.signal.aborted) return;
      try {
        const page0 = await onServerSearch(q, 0);
        if (controller.signal.aborted) return;
        setSearchResults(page0);
        setSearchHasMore((page0 || []).length >= 50);
        // Build lightweight live preview of top 5 name/company matches
        const top = (page0 || []).slice(0, 5).map(c => ({ id: c.id!, name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || (c.nickname || 'Unknown'), company: c.company || c.company_name || c.business_name || undefined }));
        setLivePreview(top);
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return;
        console.error('Search failed', err);
      } finally {
        if (!controller.signal.aborted) setSearchLoading(false);
      }
    }, 200);

    return () => {
      clearTimeout(id);
      try { controller.abort(); } catch { /* ignore */ }
    };
  }, [searchTerm, onServerSearch, onLoadAll, allContactsLoaded]);

  const hasResetRef = useRef(false);
  const isResettingRef = useRef(false);
  useEffect(() => {
    const noFiltersActive =
      collectionFilter.length === 0 &&
      statusFilter.length === 0 &&
      locationFilter.length === 0 &&
      searchTerm === '' &&
      sortBy === 'name';
    if (noFiltersActive && allContactsLoaded && !contactsLoading && !isResettingRef.current) {
      if (!hasResetRef.current) {
        isResettingRef.current = true;
        hasResetRef.current = true;
        setAllContactsLoaded(false);
        onResetToBasicView?.();
        setTimeout(() => {
          isResettingRef.current = false;
        }, 1000);
      }
    } else {
      hasResetRef.current = false;
    }
  }, [collectionFilter, statusFilter, locationFilter, sortBy, searchTerm, allContactsLoaded, contactsLoading, onResetToBasicView]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
      if (isSearching) {
        if (!searchLoading && searchHasMore && isNearBottom && onServerSearch) {
          const nextPage = searchPage + 1;
          setSearchLoading(true);
          onServerSearch((searchTerm || '').trim(), nextPage)
            .then((rows) => {
              setSearchResults(prev => [...prev, ...rows]);
              setSearchPage(nextPage);
              setSearchHasMore((rows || []).length >= 50);
            })
            .catch((err) => console.error('Search pagination failed', err))
            .finally(() => setSearchLoading(false));
        }
      } else {
        const isBasicView =
          collectionFilter.length === 0 &&
          statusFilter.length === 0 &&
          locationFilter.length === 0 &&
          searchTerm === '' &&
          sortBy === 'name';
        if (!isBasicView || contactsLoading || !hasMoreContacts || allContactsLoaded || !onLoadMore) return;
        if (isNearBottom) onLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    const handleWindowScroll = () => {
      const windowScrolledToBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100;
      if (!windowScrolledToBottom) return;
      if (isSearching) {
        if (!searchLoading && searchHasMore && onServerSearch) {
          const nextPage = searchPage + 1;
          setSearchLoading(true);
          onServerSearch((searchTerm || '').trim(), nextPage)
            .then((rows) => {
              setSearchResults(prev => [...prev, ...rows]);
              setSearchPage(nextPage);
              setSearchHasMore((rows || []).length >= 50);
            })
            .catch((err) => console.error('Search pagination failed', err))
            .finally(() => setSearchLoading(false));
        }
      } else {
        if (collectionFilter.length === 0 && searchTerm === '' && !contactsLoading && hasMoreContacts && !allContactsLoaded && onLoadMore) {
          onLoadMore();
        }
      }
    };
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [collectionFilter, statusFilter, locationFilter, sortBy, searchTerm, contactsLoading, hasMoreContacts, onLoadMore, allContactsLoaded, isSearching, searchHasMore, searchLoading, onServerSearch, searchPage]);

  const filteredContacts = useMemo(() => {
    if (isSearching && onServerSearch) {
      // When server-side searching, display those results directly
      return searchResults;
    }
    const uniqueContactsMap = new Map<string, Contact>();
    contacts.forEach(contact => { if (contact.id && !uniqueContactsMap.has(contact.id)) uniqueContactsMap.set(contact.id, contact); });
    const uniqueContacts = Array.from(uniqueContactsMap.values());

    const matchesNameTokens = (contact: Contact, input: string) => {
      const s = input.trim().toLowerCase().replace(/\s+/g, ' ');
      if (!s) return true;
      const tokens = s.split(' ').filter(Boolean);
      const fn = (contact.first_name || '').toLowerCase();
      const ln = (contact.last_name || '').toLowerCase();
      const nn = (contact.nickname || '').toLowerCase();
      const pre = (contact.name_prefix || '').toLowerCase();
      const suf = (contact.name_suffix || '').toLowerCase();
      const full = `${fn} ${ln}`.trim().replace(/\s+/g, ' ');
      const rev = `${ln} ${fn}`.trim().replace(/\s+/g, ' ');
      const fields = [fn, ln, nn, pre, suf, full, rev];
      return tokens.every(t => fields.some(f => f.includes(t)));
    };

    const q = (searchTerm || '').trim().toLowerCase();
    const matchingCollectionIds = new Set<string>((collections || []).filter(col => col.name.toLowerCase().includes(q)).map(c => c.id));
    const updatedDays = (() => { const m = q.match(/(\d+)d/); if (!m) return undefined; const n = Number(m[1]); return isNaN(n) ? undefined : n; })();

    let filtered = uniqueContacts.filter(contact => {
      if (contact.id && hiddenContactIds.has(contact.id)) return false;
      if (contact.is_archived) return false;
      if (collectionSelectionMode?.enabled && collectionSelectionMode.existingMemberIds.includes(contact.id!)) return false;
      if (!q) return true;

      const checks: Record<FieldFilter, boolean> = {
        name: matchesNameTokens(contact, q),
        company: ((contact.company || contact.company_name || contact.business_name || '').toLowerCase().includes(q)),
        location: (() => {
          const locationStrings = [
            contact.city && contact.state ? `${contact.city}, ${contact.state}` : undefined,
            contact.address_1_city && contact.address_1_region ? `${contact.address_1_city}, ${contact.address_1_region}` : undefined,
            contact.business_city && contact.business_state ? `${contact.business_city}, ${contact.business_state}` : undefined,
            contact.city,
            contact.address_1_city,
            contact.business_city,
            contact.state,
            contact.address_1_region,
            contact.business_state,
            contact.location_info,
          ].filter(Boolean) as string[];
          return locationStrings.some(ls => (ls || '').toLowerCase().includes(q));
        })(),
        collection: (() => {
          if (matchingCollectionIds.size === 0) return false;
          const contactCollections = contactCollectionMap.get(contact.id || '') || [];
          return contactCollections.some(id => matchingCollectionIds.has(id));
        })(),
        status: (contact.status || '').toLowerCase().includes(q),
        relationship: (contact.relationship_type || '').toLowerCase().includes(q),
        updated: (() => {
          if (!updatedDays || updatedDays <= 0) return false;
          const threshold = Date.now() - updatedDays * 24 * 60 * 60 * 1000;
          const ts = new Date(contact.updated_at || contact.created_at || '').getTime();
          return !isNaN(ts) && ts >= threshold;
        })(),
      };

      return selectedFieldFilters.some(f => checks[f]);
    });

    const effectiveSort = sortBy;
    filtered.sort((a, b) => {
      switch (effectiveSort) {
        case 'name':
          return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
        case 'company':
          return (a.company || '').localeCompare(b.company || '');
        case 'updated':
        default:
          return new Date(b.updated_at || b.created_at || '').getTime() - new Date(a.updated_at || a.created_at || '').getTime();
      }
    });

    return filtered;
  }, [contacts, searchTerm, contactCollectionMap, sortBy, hiddenContactIds, selectedFieldFilters, collections, isSearching, onServerSearch, searchResults]);

  useEffect(() => {
    let restored = false;
    const attemptRestore = () => {
      if (restored) return;
      try {
        const key = 'gv:scroll:contacts';
        const saved = sessionStorage.getItem(key);
        if (!saved) return;
        const container = containerRef.current;
        if (!container) return;
        requestAnimationFrame(() => {
          container.scrollTo({ top: Number(saved) || 0, behavior: 'instant' as any });
          sessionStorage.removeItem(key);
        });
        restored = true;
      } catch { }
    };
    const id = setTimeout(attemptRestore, 0);
    return () => clearTimeout(id);
  }, [filteredContacts.length, contactsLoading]);

  const togglePriority = async (contactId: string, currentPriority: boolean) => {
    try {
      await updateContactCore(supabase as any, contactId, { is_priority: !currentPriority });
      onContactUpdate?.();
    } catch (error) {
      console.error('Error updating contact priority:', error);
    }
  };

  const getContactLocation = (contact: Contact) => {
    if (contact.state) return contact.state;
    if (contact.address_1_region) return contact.address_1_region;
    if (contact.business_state) return contact.business_state;
    if (contact.city && contact.state) return `${contact.city}, ${contact.state}`;
    if (contact.address_1_city && contact.address_1_region) return `${contact.address_1_city}, ${contact.address_1_region}`;
    if (contact.business_city && contact.business_state) return `${contact.business_city}, ${contact.business_state}`;
    if (contact.city) return contact.city;
    if (contact.address_1_city) return contact.address_1_city;
    if (contact.business_city) return contact.business_city;
    if (contact.location_info) return contact.location_info;
    return null;
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedContacts(new Set());
  };

  const toggleContactSelection = (contactId: string) => {
    if (collectionSelectionMode?.enabled) {
      const newSelection = new Set(selectedContactIds);
      if (newSelection.has(contactId)) newSelection.delete(contactId); else newSelection.add(contactId);
      setSelectedContactIds(newSelection);
    } else {
      const newSelection = new Set(selectedContacts);
      if (newSelection.has(contactId)) newSelection.delete(contactId); else newSelection.add(contactId);
      setSelectedContacts(newSelection);
    }
  };

  const selectAllVisible = () => {
    const visibleIds = new Set(filteredContacts.map(c => c.id!));
    setSelectedContacts(visibleIds);
  };
  const clearSelection = () => setSelectedContacts(new Set());

  const handleContactClick = (contact: Contact) => {
    if (selectionMode || collectionSelectionMode?.enabled) {
      toggleContactSelection(contact.id!);
    } else {
      try {
        const pos = containerRef.current ? (containerRef.current.scrollTop || 0) : 0;
        sessionStorage.setItem('gv:scroll:contacts', String(pos));
      } catch { }
      onContactSelect(contact);
    }
  };

  const openContextMenu = (e: React.MouseEvent, contact: Contact) => {
    e.preventDefault();
    const x = e.clientX; const y = e.clientY;
    setContextMenu({ x, y, contact });
  };
  const closeContextMenu = () => setContextMenu(null);
  useEffect(() => {
    const onGlobal = () => {
      setContextMenu(null);
      // Also close live preview suggestions when clicking elsewhere
      setLivePreview([]);
    };
    window.addEventListener('click', onGlobal);
    window.addEventListener('scroll', onGlobal, { passive: true });
    return () => { window.removeEventListener('click', onGlobal); window.removeEventListener('scroll', onGlobal); };
  }, []);

  let longPressTimer: any = null;
  const handleTouchStart = (e: React.TouchEvent, contact: Contact) => {
    const touch = e.touches[0];
    longPressTimer = setTimeout(() => { setContextMenu({ x: touch.clientX, y: touch.clientY, contact }); }, 500);
  };
  const handleTouchEnd = () => { if (longPressTimer) clearTimeout(longPressTimer); };

  const archiveContact = async (contactId: string) => {
    try {
      await toggleArchiveContactCore(supabase as any, contactId, true);
      showNotification('Contact archived', 'success');
      setHiddenContactIds(prev => new Set(prev).add(contactId));
      try {
        window.dispatchEvent(new CustomEvent('gv:contact-removed', { detail: { id: contactId, reason: 'archived' } }));
        const key = 'gv:removedContactIds';
        const raw = sessionStorage.getItem(key);
        const arr = raw ? (JSON.parse(raw) as string[]) : [];
        if (!arr.includes(contactId)) {
          arr.push(contactId);
          sessionStorage.setItem(key, JSON.stringify(arr));
        }
      } catch { }
      onContactUpdate?.(null as any);
      setContextMenu(null);
    } catch (err) {
      console.error('Archive failed', err);
      showNotification('Failed to archive', 'error');
    }
  };

  const deleteContact = async (contactId: string) => {
    setPendingDeleteId(contactId);
  };

  const confirmDelete = async () => {
    const contactId = pendingDeleteId;
    if (!contactId) return;
    try {
      await deleteContactCore(supabase as any, contactId);
      showNotification('Contact deleted', 'success');
      setHiddenContactIds(prev => new Set(prev).add(contactId));
      try {
        window.dispatchEvent(new CustomEvent('gv:contact-removed', { detail: { id: contactId, reason: 'deleted' } }));
        const key = 'gv:removedContactIds';
        const raw = sessionStorage.getItem(key);
        const arr = raw ? (JSON.parse(raw) as string[]) : [];
        if (!arr.includes(contactId)) {
          arr.push(contactId);
          sessionStorage.setItem(key, JSON.stringify(arr));
        }
      } catch { }
      onContactUpdate?.(null as any);
      setContextMenu(null);
      setPendingDeleteId(null);
    } catch (err) {
      console.error('Delete failed', err);
      showNotification('Failed to delete', 'error');
      setPendingDeleteId(null);
    }
  };

  const openCollectionSelector = () => {
    if (selectedContacts.size === 0) {
      showNotification('Please select at least one contact', 'error');
      return;
    }
    setIsCollectionSelectorOpen(true);
  };

  const handleContactsAddedToCollection = () => {
    setSelectedContacts(new Set());
    setSelectionMode(false);
    setIsCollectionSelectorOpen(false);
  };

  const handleAddSelectedToCollection = async () => {
    if (!collectionSelectionMode?.enabled || selectedContactIds.size === 0) return;
    try {
      const ops = Array.from(selectedContactIds).map(contactId => ({ collection_id: collectionSelectionMode.collectionId, contact_id: contactId, added_by: user?.id || undefined }));
      await bulkAddMembersCore(supabase as any, ops);
      onNavigateBack?.();
    } catch (error) {
      console.error('Error adding contacts to collection:', error);
      showNotification('Failed to add contacts to collection. Please try again.', 'error');
    }
  };

  return (
    <>
      {notification && (<NotificationToast notification={notification} onClose={hideNotification} />)}

      {collectionSelectionMode?.enabled && (
        <div className="fixed top-14 lg:top-16 left-0 right-0 bg-gray-900 border-b border-gray-700 p-4 z-40 shadow-lg">
          <div className="space-y-3">
            <div className="text-center">
              <span className="text-sm font-medium text-white">{selectedContactIds.size} contact{selectedContactIds.size !== 1 ? 's' : ''} selected</span>
            </div>
            <div className="flex justify-center gap-3">
              <button onClick={handleAddSelectedToCollection} disabled={selectedContactIds.size === 0} className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 disabled:bg-gray-600 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-sm tracking-wide border border-blue-500 shadow-lg hover:shadow-xl">
                <Plus className="w-5 h-5 mr-2" />
                ADD {selectedContactIds.size}
              </button>
              <button onClick={() => onNavigateBack?.()} className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-br from-gray-800 to-gray-900 text-white border border-gray-700 hover:from-gray-700 hover:to-gray-800 transition-all duration-200 font-semibold text-sm tracking-wide shadow-lg hover:shadow-xl">
                <X className="w-5 h-5 mr-2" />
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      <div ref={containerRef} className="space-y-6 h-full overflow-y-auto" style={{ paddingTop: collectionSelectionMode?.enabled ? '7rem' : '0' }}>
        {/* Header & Controls */}
        <div className={`bg-gradient-to-r from-gray-900 to-black border border-gray-800 p-6 lg:p-8 text-white ${collectionSelectionMode?.enabled ? 'mt-4' : ''}`}>
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              {collectionSelectionMode?.enabled ? (
                <>
                  <h2 className="text-2xl lg:text-3xl font-light mb-2 tracking-wide">Add Contacts</h2>
                  <p className="text-gray-300 text-base lg:text-lg font-light">Select contacts to add to <span className="text-blue-400 font-medium">{collectionSelectionMode.collectionName}</span></p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl lg:text-3xl font-light mb-2 tracking-wide">Contact Directory</h2>
                  <p className="text-gray-300 text-base lg:text-lg font-light">Manage and organize your family office contacts</p>
                  {selectionMode && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-900/50 text-green-300 border border-green-700/50">
                        <Check className="w-4 h-4 mr-2" />
                        {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} selected
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {collectionSelectionMode?.enabled ? (
                <div></div>
              ) : (
                <>
                  {user && (
                    <button onClick={toggleSelectionMode} className={`inline-flex items-center justify-center px-4 py-2 border transition-colors font-medium text-sm tracking-wide ${selectionMode ? 'bg-green-600 text-white border-green-500 hover:bg-green-700' : 'bg-gray-800 text-white border-gray-700 hover:bg-gray-700'}`}>
                      {selectionMode ? (<><Check className="w-4 h-4 mr-2" />EXIT SELECT</>) : (<><UserPlus className="w-4 h-4 mr-2" />SELECT MODE</>)}
                    </button>
                  )}
                  <button onClick={onAddContact} className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide">
                    <Plus className="w-5 h-5 mr-2" />
                    NEW CONTACT
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Filters - Sticky (can be hidden to use external quick input UI) */}
        {!hideSearchHeader && (
          <div className={`bg-black border border-gray-800 p-6 sticky z-30 ${collectionSelectionMode?.enabled ? 'top-36 pt-20' : 'top-16 lg:top-20'}`}>
            <div className="space-y-4">
              {selectionMode && (
                <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-300">{selectedContacts.size} of {filteredContacts.length} selected</span>
                    <button onClick={selectAllVisible} className="text-sm text-blue-400 hover:text-blue-300">Select All</button>
                    <button onClick={clearSelection} className="text-sm text-gray-400 hover:text-gray-300">Clear</button>
                  </div>
                  <button onClick={openCollectionSelector} disabled={selectedContacts.size === 0} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed">
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Add to Collection
                  </button>
                </div>
              )}

              {/* Search + filter icon (in-input) */}
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  {/* Reserve space under input for dropdown to minimize layout shift */}
                  <div className="relative pb-0">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={"Search"}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
                      onBlur={() => {
                        // Hide live preview suggestions when losing focus
                        setTimeout(() => setLivePreview([]), 150);
                      }}
                      onFocus={() => {
                        // Restore live preview suggestions when gaining focus
                        if (searchTerm && searchTerm.length >= 2) {
                          // Trigger the live preview logic again
                          const timer = setTimeout(() => {
                            const top = filteredContacts.slice(0, 8).map(c => ({
                              id: c.id!,
                              name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                              company: c.company || undefined
                            }));
                            setLivePreview(top);
                          }, 100);
                          return () => clearTimeout(timer);
                        }
                      }}
                      className="w-full pl-10 pr-24 py-2 bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-gray-600 rounded-md"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 z-30">
                      <button
                        ref={filterButtonRef}
                        type="button"
                        onClick={() => setShowFieldDropdown(v => !v)}
                        className="w-9 h-9 grid place-items-center rounded-md text-gray-300 hover:text-white hover:bg-gray-800/60 focus:outline-none focus:ring-2 focus:ring-gray-600"
                        title="Toggle filters"
                        aria-label="Toggle filters"
                      >
                        <Filter className="w-5 h-5" />
                      </button>
                      {searchTerm && (
                        <button type="button" aria-label="Clear search" title="Clear" onClick={() => setSearchTerm('')} className="w-7 h-7 grid place-items-center rounded bg-gray-800 text-gray-200 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600">×</button>
                      )}
                    </div>
                    {/* Live preview panel */}
                    {livePreview.length > 0 && (
                      <div className="absolute left-0 right-0 mt-2 bg-black border border-gray-800 rounded-md shadow-lg z-20">
                        {livePreview.map(item => (
                          <button
                            key={item.id}
                            className="w-full text-left px-3 py-2 hover:bg-gray-900 flex items-center justify-between"
                            onClick={() => {
                              const el = document.querySelector(`[data-contact-id="${item.id}"]`) as HTMLElement | null;
                              if (el) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                el.classList.add('ring-2', 'ring-blue-500');
                                setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500'), 1500);
                              }
                            }}
                          >
                            <span className="text-gray-100">{item.name}</span>
                            {item.company && <span className="text-sm text-gray-400 ml-2">{item.company}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {showFieldDropdown && (
                    <div ref={filterDropdownRef} className="mt-2 w-full bg-gray-900 border border-gray-700 rounded-lg shadow-lg relative z-20 p-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {allFieldFilters.map(ff => {
                          const checked = selectedFieldFilters.includes(ff);
                          const label = ff === 'name' ? 'Name' : ff === 'company' ? 'Company' : ff === 'location' ? 'Location' : ff === 'collection' ? 'Collection' : ff === 'status' ? 'Status' : ff === 'relationship' ? 'Relationship' : 'Updated';
                          return (
                            <button
                              key={ff}
                              type="button"
                              className={
                                `w-full px-3 py-2 rounded-md text-sm border transition-colors ` +
                                (checked
                                  ? 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 text-white border-gray-500 hover:from-gray-800 hover:to-gray-600'
                                  : 'bg-gray-800 text-gray-200 border-gray-700 hover:bg-gray-700')
                              }
                              onClick={() => {
                                setSelectedFieldFilters(prev => prev.includes(ff) ? prev.filter(x => x !== ff) : [...prev, ff]);
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex justify-between items-center pt-2 mt-3 border-t border-gray-700">
                        <button type="button" className="text-xs px-2 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800" onClick={() => setSelectedFieldFilters(allFieldFilters)}>Select all</button>
                        <button type="button" className="text-xs px-2 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800" onClick={() => setSelectedFieldFilters([])}>Clear</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-3 md:gap-6">
          {filteredContacts.map((contact) => {
            const isSelected = (collectionSelectionMode?.enabled) ? selectedContactIds.has(contact.id!) : selectedContacts.has(contact.id!);
            return (
              <button
                key={contact.id}
                data-contact-id={contact.id}
                onClick={() => handleContactClick(contact)}
                onContextMenu={(e) => openContextMenu(e, contact)}
                onTouchStart={(e) => handleTouchStart(e, contact)}
                onTouchEnd={handleTouchEnd}
                className={`bg-black border p-3 md:p-6 hover:bg-gray-900 transition-all duration-200 text-left group rounded-lg relative ${(selectionMode || collectionSelectionMode?.enabled) && isSelected ? 'border-blue-500 bg-blue-500/10' : 'border-gray-800'}`}
              >
                {(selectionMode || collectionSelectionMode?.enabled) && (
                  <div className="absolute top-2 left-2 z-10">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'bg-gray-900 border-gray-600'}`}>
                      {isSelected && (<Check className="w-4 h-4 text-white" />)}
                    </div>
                  </div>
                )}

                <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-3 md:space-y-0">
                  <div className="w-28 h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform mx-auto md:mx-0 shadow-lg relative">
                    {contact.profile_image_url ? (
                      <img src={contact.profile_image_url} alt={`${contact.first_name} ${contact.last_name}`} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-gray-300 font-semibold text-xl md:text-2xl lg:text-3xl">{contact.first_name?.[0]}{contact.last_name?.[0]}</span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 text-center md:text-left">
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                          if (!name) return;
                          await navigator.clipboard.writeText(name);
                          showNotification('Name copied', 'success');
                        } catch { showNotification('Failed to copy name', 'error'); }
                        const btn = e.currentTarget as HTMLButtonElement;
                        setTimeout(() => { try { btn.blur(); } catch { } }, 1000);
                      }}
                      title="Click to copy name"
                      className="w-28 md:w-auto mx-auto md:mx-0 text-sm md:text-xl lg:text-2xl font-light text-white mb-1 leading-tight break-words hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-600 rounded"
                    >
                      {contact.first_name} {contact.last_name}
                    </button>

                    {getContactLocation(contact) && (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const loc = getContactLocation(contact);
                            if (!loc) return;
                            await navigator.clipboard.writeText(loc);
                            showNotification('Location copied', 'success');
                          } catch { showNotification('Failed to copy location', 'error'); }
                          const btn = e.currentTarget as HTMLButtonElement;
                          setTimeout(() => { try { btn.blur(); } catch { } }, 1000);
                        }}
                        title="Click to copy location"
                        className="block w-28 md:w-auto mx-auto md:mx-0 text-left text-xs md:text-sm text-gray-400 mb-1 md:mb-2 break-words hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-600 rounded"
                      >
                        {getContactLocation(contact)}
                      </button>
                    )}

                    {contact.is_priority && (
                      <div className="flex justify-center md:justify-start mb-2">
                        <Star className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 fill-current" />
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {filteredContacts.length === 0 && (
          <div className="text-center py-12 bg-black border border-gray-800">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-light text-white mb-2 tracking-wide">No contacts found</h3>
            <p className="text-gray-400 mb-6 font-light">{searchTerm || collectionFilter.length > 0 || statusFilter.length > 0 || locationFilter.length > 0 ? 'Try adjusting your filters or search terms' : 'Add your first contact to get started'}</p>
            <button onClick={onAddContact} className="inline-flex items-center px-6 py-3 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide">
              <Plus className="w-5 h-5 mr-2" />
              NEW CONTACT
            </button>
          </div>
        )}

        {(contactsLoading || searchLoading) && filteredContacts.length > 0 && (
          <div className="text-center py-6 bg-black border border-gray-800 border-t-0">
            <div className="flex items-center justify-center gap-2">
              {/* Square-friendly loader: three pulsing squares */}
              <div className="flex items-center gap-1 mr-2" aria-hidden>
                <div className="w-2.5 h-2.5 bg-white animate-pulse" style={{ animationDelay: '0ms' }} />
                <div className="w-2.5 h-2.5 bg-white animate-pulse" style={{ animationDelay: '150ms' }} />
                <div className="w-2.5 h-2.5 bg-white animate-pulse" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-gray-300 text-sm font-medium tracking-wide">LOADING MORE CONTACTS...</span>
            </div>
          </div>
        )}

        {!contactsLoading && !hasMoreContacts && filteredContacts.length > 0 && collectionFilter.length === 0 && searchTerm === '' && (
          <div className="text-center py-6 bg-black border border-gray-800 border-t-0">
            <div className="flex items-center justify-center text-gray-400">
              <Check className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium tracking-wide">ALL CONTACTS LOADED</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">You've reached the end of your contact list</p>
          </div>
        )}

        {isCollectionSelectorOpen && user && (
          <CollectionSelectorModal
            selectedContactIds={Array.from(selectedContacts)}
            user={user}
            onClose={() => setIsCollectionSelectorOpen(false)}
            onContactsAdded={handleContactsAddedToCollection}
          />
        )}

        {contextMenu && (
          <div className="fixed z-50 bg-gray-900 border border-gray-700 rounded-md shadow-xl" style={{ left: contextMenu.x + 4, top: contextMenu.y + 4, minWidth: 200 }}>
            <button className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 flex items-center gap-2" onClick={() => { setSelectedContacts(new Set([contextMenu.contact.id!])); setIsCollectionSelectorOpen(true); setContextMenu(null); }}>
              <FolderPlus className="w-4 h-4" /> Add to Collection
            </button>
            <div className="border-t border-gray-700">
              <button 
                className="w-full text-left px-3 py-2 text-blue-300 hover:bg-gray-700 flex items-center gap-2" 
                onClick={async () => {
                  setContextMenu(null);
                  try {
                    await exportContactToPDF(contextMenu.contact);
                  } catch (error) {
                    showNotification({ type: 'error', message: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}` });
                  }
                }}
              >
                <FileDown className="w-4 h-4" /> Export as PDF
              </button>
              <button 
                className="w-full text-left px-3 py-2 text-blue-300 hover:bg-gray-700 flex items-center gap-2" 
                onClick={async () => {
                  setContextMenu(null);
                  try {
                    await exportContactToHTML(contextMenu.contact);
                    showNotification({ type: 'success', message: 'Contact exported successfully!' });
                  } catch (error) {
                    showNotification({ type: 'error', message: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}` });
                  }
                }}
              >
                <FileDown className="w-4 h-4" /> Export as HTML
              </button>
            </div>
            <div className="border-t border-gray-700">
              <button className="w-full text-left px-3 py-2 text-yellow-300 hover:bg-gray-700 flex items-center gap-2" onClick={() => archiveContact(contextMenu.contact.id!)}>
                <Archive className="w-4 h-4" /> Archive
              </button>
              <button className="w-full text-left px-3 py-2 text-red-300 hover:bg-gray-700 flex items-center gap-2" onClick={() => deleteContact(contextMenu.contact.id!)}>
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        )}
        <ConfirmDialog
          open={!!pendingDeleteId}
          title="Delete Contact"
          message={`Permanently delete this contact?\nThis action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          destructive
          onConfirm={confirmDelete}
          onCancel={() => setPendingDeleteId(null)}
        />
      </div>
    </>
  );
}